import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchLeadStagesListV2 } from 'hooks/queries/useLeadStagesListV2Query';
import { createLeadStages } from 'services/Settings';
import { extractPracticeAreaIds } from 'utils/utils';
import { queryKeys } from '../../config/queryClient';

const LEAD_STAGE_BOARD_LIMIT = 1000;

const fetchLeadStages = async (practice_area, show_unassigned_pa_column) => {
  const result = await fetchLeadStagesListV2({
    excludeFromLoading: true,
    limit: LEAD_STAGE_BOARD_LIMIT,
    page: 1,
    sort_by: 'lead_stage_name',
    sort_order: 'asc',
    keyword: '',
    lead_stage_status: 'ACTIVE',
    practice_area: extractPracticeAreaIds(practice_area),
    show_unassigned_pa_column,
  });
  return result?.rows ?? [];
};

const transformLeadStagesToColumns = (leadStages) => {
  if (!leadStages || !Array.isArray(leadStages)) {
    return [];
  }

  const activeStages = leadStages.filter((stage) => stage.status === 'ACTIVE').sort((a, b) => a.position - b.position);

  const newStage = activeStages.find((stage) => stage.lead_stage_name === 'New');
  const closedStage = activeStages.find((stage) => stage.lead_stage_name === 'Closed');
  const otherStages = activeStages.filter((stage) => stage.lead_stage_name !== 'New' && stage.lead_stage_name !== 'Closed');

  const orderedStages = [];
  if (newStage) orderedStages.push(newStage);
  orderedStages.push(...otherStages);
  if (closedStage) orderedStages.push(closedStage);

  return orderedStages.map((stage, index) => ({
    ...stage,
    isDefault: stage.is_default,
    isLocked: stage.lead_stage_name === 'New' || stage.lead_stage_name === 'Closed',
  }));
};

/**
 * Hook to fetch lead stages with React Query
 *
 * @returns {Object} - leadStages, isLoading, error, refetch
 */
export const useLeadStagesQuery = (practice_area, show_unassigned_pa_column) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.leadStages.list(practice_area, show_unassigned_pa_column),
    queryFn: () => fetchLeadStages(practice_area, show_unassigned_pa_column),
    staleTime: 10 * 60 * 1000, // Consider data stale after 10 minutes
    cacheTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus for relatively static data
    retry: 2, // Retry twice on failure
  });

  return {
    leadStages: transformLeadStagesToColumns(data) || [],
    isLeadStageLoading: isLoading,
    error,
    refetch,
  };
};

/**
 * Hook to create a new lead stage with React Query mutation
 *
 * @returns {Object} - mutation object with mutate, mutateAsync, isLoading, error, etc.
 */
export const useCreateLeadStageMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const normalizedData = {
        ...data,
        practice_area: extractPracticeAreaIds(data?.practice_area),
      };
      const response = await createLeadStages(normalizedData);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch all lead stages queries after successful creation
      queryClient.invalidateQueries(queryKeys.leadStages.all);
    },
  });
};
